import { useCallback, useEffect, useMemo, useState } from "react";

import { Anchor, Box, Button, Flex, Group, Modal, Textarea, Tooltip } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ConfirmationModal from "components/modal/confirmation";
import SuccessModal from "components/modal/success";

import { IRootState } from "config/redux";
import { AppConstants, ServiceConstants } from "constant";
import { useServiceSuccess } from "hooks";
import { ChevronSelect } from "pages/todo/list/new/DateTimeSelect";
import { AbsenceService, UserService } from "services";
import { HolidayResponse, UserResponse } from "types";
import { DateUtils, StorageUtils } from "utils";

import classes from "./AbsenceRequestModal.module.css";

const { STORAGE_USER } = AppConstants;
const { 
  REQUEST_ABSENCE_CREATE_ID, 
  REQUEST_ABSENCE_VIEW_ID,
  REQUEST_ABSENCE_UPDATE_ID,
  REQUEST_ABSENCE_DELETE_ID,
  REQUEST_ABSENCE_CANCEL_ID,
  REQUEST_ABSENCE_REPLACEMENT_AGENTS_ID,
  REQUEST_USERS_LIST_ID,
  REQUEST_USERS_CLIENTS_ABSENCE_AVAILABLE_ID,
} = ServiceConstants;

interface AbsenceFormValues {
  dateRange: [Date | null, Date | null];
  reason: string | null;
  replacementAgentId: string | null;
  note: string;
}

interface AbsenceRequestModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  absenceId?: number | null;
  holidays?: HolidayResponse[];
}

const AbsenceRequestModal = ({ opened, onClose, onSuccess, absenceId, holidays = [] }: AbsenceRequestModalProps) => {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<{ value: string; label: string }[]>([]);
  const [absenceStatus, setAbsenceStatus] = useState<string | null>(null);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);
  const [successModalOpened, setSuccessModalOpened] = useState(false);

  const [loggedUser] = useState<UserResponse | undefined>(StorageUtils.getObject(STORAGE_USER));

  const isEditMode = !!absenceId;

  // Create a map for quick holiday lookup
  const holidayMap = useMemo(() => {
    const map = new Map<string, string>();
    holidays.forEach((h) => {
      map.set(h.date, h.name);
    });
    return map;
  }, [holidays]);

  // Form state using Mantine useForm
  const form = useForm<AbsenceFormValues>({
    mode: 'controlled',
    initialValues: {
      dateRange: [null, null],
      reason: null,
      replacementAgentId: null,
      note: '',
    },
  });

  // Reset form helper
  const resetForm = useCallback(() => {
    form.reset();
    setAbsenceStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Success handler for create/update
  const handleSuccess = useCallback(() => {
    resetForm();
    onClose();
    onSuccess?.();
  }, [onClose, onSuccess, resetForm]);

  // Success handler for delete/cancel
  const handleDeleteSuccess = useCallback(() => {
    setConfirmModalOpened(false);
    setSuccessModalOpened(true);
  }, []);

  // Service success hooks - replace manual useRef/useEffect patterns
  const { isLoading: isCreateLoading } = useServiceSuccess(REQUEST_ABSENCE_CREATE_ID, { 
    onSuccess: handleSuccess 
  });
  const { isLoading: isUpdateLoading } = useServiceSuccess(
    absenceId ? REQUEST_ABSENCE_UPDATE_ID + absenceId : undefined, 
    { onSuccess: handleSuccess }
  );
  const { isLoading: isDeleteLoading } = useServiceSuccess(
    absenceId ? REQUEST_ABSENCE_DELETE_ID + absenceId : undefined, 
    { onSuccess: handleDeleteSuccess }
  );
  const { isLoading: isCancelLoading } = useServiceSuccess(
    absenceId ? REQUEST_ABSENCE_CANCEL_ID + absenceId : undefined, 
    { onSuccess: handleDeleteSuccess }
  );

  const viewServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_ABSENCE_VIEW_ID]);
  const usersServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_LIST_ID]);
  const replacementAvailabilityServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_ABSENCE_AVAILABLE_ID],
  );
  const replacementAgentsServiceObject = useSelector(
    (state: IRootState) => state.service.object[REQUEST_ABSENCE_REPLACEMENT_AGENTS_ID],
  );
  const canViewReplacementClients = replacementAvailabilityServiceObject?.data === true;

  const isLoading = isCreateLoading || isUpdateLoading || isDeleteLoading || isCancelLoading;

  // Reset form when opening in create mode (no absenceId)
  useEffect(() => {
    if (opened && !absenceId) {
      resetForm();
    }
  }, [opened, absenceId, resetForm]);

  // Fetch agents list when modal opens. Agents covering another agent use the dedicated endpoint.
  useEffect(() => {
    if (opened && replacementAvailabilityServiceObject?.status === 'SUCCESS') {
      if (canViewReplacementClients) {
        AbsenceService.absenceReplacementAgents({ page: 0, pageSize: 20, search: '' });
      } else {
        UserService.usersAll({ page: 0, search: '', profileId: 2, status: 'ACTIVE', type: 'EMPLOYEE' });
      }
    }
  }, [opened, canViewReplacementClients, replacementAvailabilityServiceObject?.status]);

  // Fetch absence data when editing
  useEffect(() => {
    if (opened && absenceId) {
      AbsenceService.absenceOne(absenceId);
    }
  }, [opened, absenceId]);

  // Populate form when editing
  useEffect(() => {
    if (viewServiceObject?.status === 'SUCCESS' && viewServiceObject?.data && isEditMode) {
      const absence = viewServiceObject.data;
      form.setValues({
        dateRange: [new Date(absence.dateFrom), new Date(absence.dateTo)],
        reason: absence.reason || null,
        replacementAgentId: absence.replacementAgent?.id ? String(absence.replacementAgent.id) : null,
        note: absence.note || "",
      });
      setAbsenceStatus(absence.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewServiceObject, isEditMode]);

  // Process agents list
  useEffect(() => {
    const agentsServiceObject = canViewReplacementClients
      ? replacementAgentsServiceObject
      : usersServiceObject;

    if (agentsServiceObject?.status === 'SUCCESS' && agentsServiceObject?.data?.content) {
      const agentOptions = agentsServiceObject.data.content
        .filter((agent: UserResponse) => agent.id !== loggedUser?.id) // Exclude self
        .map((agent: UserResponse) => ({
          value: String(agent.id),
          label: agent.fullName,
        }));
      setAgents(agentOptions);
    }
  }, [canViewReplacementClients, replacementAgentsServiceObject, usersServiceObject, loggedUser?.id]);

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = useCallback(() => {
    const { dateRange, reason, replacementAgentId, note } = form.values;
    if (!dateRange[0] || !dateRange[1] || !loggedUser?.id) return;

    const data = {
      agentId: loggedUser.id,
      dateFrom: formatDate(dateRange[0]),
      dateTo: formatDate(dateRange[1]),
      reason: reason || undefined,
      replacementAgentId: replacementAgentId ? Number(replacementAgentId) : undefined,
      note: note || undefined,
    };

    if (isEditMode && absenceId) {
      AbsenceService.absenceUpdate(absenceId, data);
    } else {
      AbsenceService.absenceCreate(data);
    }
  }, [form.values, loggedUser?.id, isEditMode, absenceId]);

  const handleOpenConfirmModal = useCallback(() => {
    setConfirmModalOpened(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!absenceId) return;
    
    if (absenceStatus === 'PENDING') {
      AbsenceService.absenceDelete(absenceId);
    } else if (absenceStatus === 'APPROVED') {
      AbsenceService.absenceCancel(absenceId);
    }
  }, [absenceId, absenceStatus]);

  const handleSuccessModalClose = useCallback(() => {
    setSuccessModalOpened(false);
    resetForm();
    onClose();
    onSuccess?.();
  }, [onClose, onSuccess, resetForm]);

  const canSubmit = useMemo(() => {
    const { dateRange, replacementAgentId } = form.values;
    return dateRange[0] && dateRange[1] && replacementAgentId && !isLoading;
  }, [form.values, isLoading]);

  const canCancelRequest = useMemo(() => {
    return isEditMode && (absenceStatus === 'PENDING' || absenceStatus === 'APPROVED');
  }, [isEditMode, absenceStatus]);

  const reasonOptions = useMemo(
    () => [
      { value: "VACATION", label: t("profilePage.absences.reason.VACATION") },
      { value: "FREE_DAYS", label: t("profilePage.absences.reason.FREE_DAYS") },
      { value: "SICK_LEAVE", label: t("profilePage.absences.reason.SICK_LEAVE") },
      { value: "CHILD_BIRTH", label: t("profilePage.absences.reason.CHILD_BIRTH") },
    ],
    [t],
  );

  return (
    <>
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(isEditMode ? "profilePage.absences.requestModal.title.edit" : "profilePage.absences.requestModal.title.create")}
      size="lg"
      centered
      padding="xl"
      styles={{ title: { fontSize: 24, fontWeight: 600 } }}
    >
      <Flex className={classes.content} gap="xl" p="md">
        {/* Kalendar za izbor datuma */}
        <Box
          className={classes.calendarBox}
          p="md"
          style={{
            backgroundColor: isEditMode ? 'var(--mantine-color-gray-1)' : undefined,
          }}
        >
          <DatePicker
            type="range"
            allowSingleDateInRange
            value={form.values.dateRange}
            onChange={(value) => form.setFieldValue('dateRange', value)}
            color="primary"
            getDayProps={(date) => {
              const today = new Date();
              const isToday = date.toDateString() === today.toDateString();
              const { dateRange } = form.values;
              const isInRange = dateRange[0] && dateRange[1] && 
                date >= dateRange[0] && date <= dateRange[1];
              const dateString = DateUtils.formatDateLocal(date);
              const isHoliday = holidayMap.has(dateString);
              
              return {
                style: {
                  // Holiday always takes precedence (purple)
                  ...(isHoliday && {
                    backgroundColor: 'var(--mantine-color-violet-6)',
                    color: 'white',
                    borderRadius: 4,
                  }),
                  // Selected range but not holiday (green)
                  ...(isInRange && !isHoliday && {
                    backgroundColor: 'var(--mantine-color-primary-6)',
                    color: 'white',
                    borderRadius: 4,
                  }),
                  // Today marker (only if not selected and not holiday)
                  ...(isToday && !isInRange && !isHoliday && {
                    border: '2px solid var(--mantine-color-gray-5)',
                  }),
                },
              };
            }}
            renderDay={(date) => {
              const dateString = DateUtils.formatDateLocal(date);
              const holidayName = holidayMap.get(dateString);
              
              if (holidayName) {
                return (
                  <Tooltip 
                    label={holidayName} 
                    withArrow
                    color="white"
                    styles={{
                      tooltip: {
                        color: 'var(--mantine-color-violet-6)',
                        fontWeight: 500,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    <div>{date.getDate()}</div>
                  </Tooltip>
                );
              }
              return date.getDate();
            }}
          />
        </Box>

        {/* Forma */}
        <Box className={classes.formSection}>
          <ChevronSelect
            label={t("profilePage.absences.requestModal.fields.reason.label")}
            placeholder={t("profilePage.absences.requestModal.fields.reason.placeholder")}
            data={reasonOptions}
            value={form.values.reason}
            onChange={(value) => form.setFieldValue('reason', value)}
            mb="md"
            styles={isEditMode ? { input: { backgroundColor: 'var(--mantine-color-gray-1)' } } : undefined}
          />
          <ChevronSelect
            label={t("profilePage.absences.requestModal.fields.replacement.label")}
            placeholder={t("profilePage.absences.requestModal.fields.replacement.placeholder")}
            data={agents}
            value={form.values.replacementAgentId}
            onChange={(value) => form.setFieldValue('replacementAgentId', value)}
            mb="md"
            withAsterisk
            styles={isEditMode ? { input: { backgroundColor: 'var(--mantine-color-gray-1)' } } : undefined}
          />
          <Textarea
            label={t("profilePage.absences.requestModal.fields.note.label")}
            value={form.values.note}
            onChange={(e) => form.setFieldValue('note', e.currentTarget.value)}
            minRows={4}
            styles={isEditMode ? { input: { backgroundColor: 'var(--mantine-color-gray-1)' } } : undefined}
          />
        </Box>
      </Flex>

      {canCancelRequest && (
        <Anchor c="red" size="sm" ml="md" onClick={handleOpenConfirmModal} style={{ cursor: 'pointer' }}>
          {t("profilePage.absences.actions.cancelRequest")}
        </Anchor>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="outline" color="gray" radius="md" onClick={onClose}>
          {t("action.cancel")}
        </Button>
        <Button color="primary" radius="md" onClick={handleSubmit} loading={isLoading} disabled={!canSubmit}>
          {t("action.save")}
        </Button>
      </Group>
    </Modal>

    <ConfirmationModal
      opened={confirmModalOpened}
      onClose={() => setConfirmModalOpened(false)}
      onConfirm={handleConfirmDelete}
      title={t("profilePage.absences.confirmModal.title")}
      message={t("profilePage.absences.confirmModal.description")}
      confirmLabel={t("profilePage.absences.confirmModal.confirm")}
      cancelLabel={t("action.cancel")}
      loading={isLoading}
    />

    <SuccessModal
      opened={successModalOpened}
      onClose={handleSuccessModalClose}
      message={t("profilePage.absences.successModal.message")}
    />
  </>
  );
};

export default AbsenceRequestModal;
